import { useAuthStore } from '../stores';
import type { SystemPermissions } from '../types';

/**
 * Check if user has a specific permission using dot notation
 * @example useHasPermission('system.can_manage_users')
 * @example useHasPermission('modules.base-crm.access')
 */
export function useHasPermission(permissionPath: string): boolean {
  return useAuthStore((state) => state.hasPermission(permissionPath));
}

/**
 * Check if user has access to a module with minimum access level
 * @example useHasModule('base-crm', 'write')
 */
export function useHasModule(moduleId: string, minAccess?: 'read' | 'write' | 'full'): boolean {
  return useAuthStore((state) => state.hasModule(moduleId, minAccess));
}

/**
 * Check if user has a specific system permission
 * @example useHasSystemPermission('can_manage_users')
 */
export function useHasSystemPermission(permission: keyof SystemPermissions): boolean {
  return useAuthStore((state) => state.hasSystemPermission(permission));
}

/**
 * Get current user from auth store
 * Returns user data with loading state (compatible with @diggens/shared-hooks API)
 */
export function useCurrentUser() {
  const user = useAuthStore((state) => state.user);
  const isLoading = useAuthStore((state) => state.isLoading);

  return { data: user, isLoading, error: null };
}

/**
 * Get user's effective permissions
 */
export function useUserPermissions() {
  return useAuthStore((state) => state.permissions);
}

/**
 * Get user's accessible modules
 */
export function useUserModules() {
  return useAuthStore((state) => state.modules);
}

/**
 * Refresh user permissions from server
 */
export function useRefreshPermissions() {
  return useAuthStore((state) => state.refresh);
}
